import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Stack,
  Paper,
  Chip,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  LocalHospital,
  CloudQueue,
  Speed,
  Analytics,
  Security,
  Assessment,
  CheckCircle,
  ArrowForward,
  Person,
  Business,
  DataUsage,
  MedicalServices,
  Favorite
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

const Healthcare = () => {
  const caseStudies = [
    {
      title: 'Electronic Health Records Modernization',
      description: 'A major hospital system modernized their EHR platform, improving patient data accessibility by 80% and reducing administrative overhead by 30%.',
      link: '#'
    },
    {
      title: 'Telemedicine Platform Implementation',
      description: 'A healthcare network deployed a comprehensive telemedicine solution, increasing patient access to care by 150% and reducing wait times by 60%.',
      link: '#'
    },
    {
      title: 'Patient Data Security Enhancement',
      description: 'A healthcare provider implemented advanced cybersecurity measures to protect sensitive patient data, achieving 99.9% security compliance and zero data breaches.',
      link: '#'
    }
  ];

  const keyStats = [
    { value: '80%', description: 'improvement in patient data accessibility through EHR modernization.' },
    { value: '150%', description: 'increase in patient access to care through telemedicine solutions.' },
    { value: '99.9%', description: 'security compliance achieved for patient data protection.' }
  ];

  const services = [
    {
      icon: <LocalHospital sx={{ fontSize: 40, color: '#1976d2' }} />,
      title: 'Electronic Health Records',
      description: 'Modern EHR systems with seamless integration and interoperability capabilities.'
    },
    {
      icon: <MedicalServices sx={{ fontSize: 40, color: '#1976d2' }} />,
      title: 'Telemedicine Solutions',
      description: 'Comprehensive telemedicine platforms for remote patient care and consultation.'
    },
    {
      icon: <Security sx={{ fontSize: 40, color: '#1976d2' }} />,
      title: 'Data Security & Compliance',
      description: 'HIPAA-compliant security solutions for protecting sensitive patient information.'
    },
    {
      icon: <Analytics sx={{ fontSize: 40, color: '#1976d2' }} />,
      title: 'Patient Analytics',
      description: 'Advanced analytics for patient outcomes, population health, and clinical insights.'
    }
  ];

  const expert = {
    name: 'Dr. Sarah Johnson',
    title: 'Vice President, Healthcare',
    company: 'Scherstellen Technologie',
    image: '/api/placeholder/150/150',
    qa: [
      {
        question: 'How are healthcare providers delivering improved patient care through technology?',
        answer: 'Healthcare providers are implementing digital health solutions, telemedicine platforms, and AI-powered diagnostic tools that enable better patient outcomes, improved access to care, and more personalized treatment approaches.'
      },
      {
        question: 'What role does data analytics play in healthcare operations?',
        answer: 'Data analytics enables healthcare providers to improve patient outcomes, optimize clinical workflows, predict health trends, and make evidence-based decisions that enhance the quality and efficiency of care delivery.'
      },
      {
        question: 'How are healthcare organizations addressing cybersecurity challenges?',
        answer: 'Healthcare organizations are implementing comprehensive cybersecurity frameworks that include encryption, access controls, and continuous monitoring to protect sensitive patient data and ensure HIPAA compliance.'
      }
    ]
  };

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          py: 12,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 'bold',
                color: '#1a1a1a',
                mb: 3,
                lineHeight: 1.2
              }}
            >
              Healthcare
            </Typography>
            <Typography
              variant="h3"
              sx={{
                color: '#1976d2',
                mb: 2,
                fontWeight: 'bold'
              }}
            >
              Helping providers deliver improved patient care
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: '#666',
                mb: 4,
                lineHeight: 1.6,
                maxWidth: '800px',
                mx: 'auto'
              }}
            >
              Enhanced experiences and outcomes through innovative healthcare technology
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Overview Section */}
      <Box sx={{ py: 10, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 'bold',
                  color: '#1a1a1a',
                  mb: 3
                }}
              >
                Overview
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: '#666',
                  mb: 4,
                  lineHeight: 1.6
                }}
              >
                Scherstellen Technologie helps healthcare providers deliver improved patient care, experiences and outcomes through innovative technology solutions. We enable digital transformation, data-driven insights, and secure, interoperable healthcare systems.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  height: 400,
                  bgcolor: '#f5f5f5',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  Healthcare Technology Solutions
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Case Studies Section */}
      <Box sx={{ py: 10, bgcolor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 6,
              textAlign: 'center'
            }}
          >
            Success Stories
          </Typography>
          <Grid container spacing={4}>
            {caseStudies.map((study, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 'bold',
                        color: '#1a1a1a',
                        mb: 2
                      }}
                    >
                      {study.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#666',
                        mb: 3,
                        lineHeight: 1.6
                      }}
                    >
                      {study.description}
                    </Typography>
                    <Button
                      variant="outlined"
                      endIcon={<ArrowForward />}
                      sx={{
                        borderColor: '#1976d2',
                        color: '#1976d2',
                        '&:hover': {
                          borderColor: '#1565c0',
                          color: '#1565c0',
                          bgcolor: 'rgba(25, 118, 210, 0.04)'
                        }
                      }}
                    >
                      Learn more
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Key Statistics */}
      <Box sx={{ py: 10, bgcolor: '#1976d2', color: 'white' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              fontWeight: 'bold',
              mb: 6,
              textAlign: 'center'
            }}
          >
            3 things to know
          </Typography>
          <Grid container spacing={4}>
            {keyStats.map((stat, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 'bold',
                      mb: 2
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography variant="h6" sx={{ opacity: 0.9 }}>
                    {stat.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Services Section */}
      <Box sx={{ py: 10, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 6,
              textAlign: 'center'
            }}
          >
            Our Healthcare Solutions
          </Typography>
          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      {service.icon}
                      <Typography
                        variant="h5"
                        sx={{
                          ml: 2,
                          fontWeight: 'bold',
                          color: '#1a1a1a'
                        }}
                      >
                        {service.title}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#666',
                        lineHeight: 1.6
                      }}
                    >
                      {service.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Expert Q&A Section */}
      <Box sx={{ py: 10, bgcolor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 6,
              textAlign: 'center'
            }}
          >
            Industry Experts
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: '#666',
              mb: 6,
              textAlign: 'center'
            }}
          >
            Our experts, your challenges
          </Typography>
          
          <Grid container spacing={6}>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Avatar
                  src={expert.image}
                  sx={{
                    width: 150,
                    height: 150,
                    mx: 'auto',
                    mb: 3
                  }}
                />
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 'bold',
                    color: '#1a1a1a',
                    mb: 1
                  }}
                >
                  {expert.name}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: '#1976d2',
                    mb: 1
                  }}
                >
                  {expert.title}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#666'
                  }}
                >
                  {expert.company}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={8}>
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 'bold',
                    color: '#1a1a1a',
                    mb: 4
                  }}
                >
                  Q&A with Scherstellen Technologie healthcare expert, {expert.name}
                </Typography>
                
                {expert.qa.map((qa, index) => (
                  <Box key={index} sx={{ mb: 4 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 'bold',
                        color: '#1976d2',
                        mb: 2
                      }}
                    >
                      Q: {qa.question}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#666',
                        lineHeight: 1.6,
                        mb: 3
                      }}
                    >
                      A: {qa.answer}
                    </Typography>
                    {index < expert.qa.length - 1 && <Divider sx={{ my: 2 }} />}
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: 10, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: 'center',
              bgcolor: '#1976d2',
              color: 'white',
              borderRadius: 2
            }}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 'bold',
                mb: 3
              }}
            >
              Connect with an expert to discuss your IT Transformation
            </Typography>
            <Typography
              variant="h5"
              sx={{
                mb: 4,
                opacity: 0.9
              }}
            >
              Let's help you modernize and transform your healthcare operations
            </Typography>
            <Button
              variant="contained"
              size="large"
              sx={{
                bgcolor: 'white',
                color: '#1976d2',
                px: 6,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                '&:hover': {
                  bgcolor: '#f5f5f5'
                }
              }}
            >
              Contact Us
            </Button>
          </Paper>
        </Container>
      </Box>
    </>
  );
};

export default Healthcare;
